import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import storageService, { StorageType } from 'src/services/storage-service';

export const NEXT_PREVIEW_STORAGE_KEY = 'next-preview-enabled';

const inSession = { storage: StorageType.SESSION_STORAGE };

/**
 * Whether the grid-backed preview surfaces are switched on for this browser session.
 *
 * `?next=true` turns them on and `?next=false` off, and the answer is then remembered:
 * the surfaces are reached by sidebar links and Explore buttons that carry no params, so
 * a param-only switch would turn itself off on the first click. Session storage rather
 * than local so a preview left on does not quietly become someone's normal dashboard.
 *
 * Visibility only. An external user is refused by permissions whatever this returns —
 * see getPortfolioRoutesAvailabilityForCustomer, which requires both.
 */
const useNextPreview = (): boolean => {
    const [searchParams] = useSearchParams();
    const param = searchParams.get('next');

    useEffect(() => {
        if (param === null) {
            return;
        }
        storageService.save(NEXT_PREVIEW_STORAGE_KEY, param === 'true', inSession);
    }, [param]);

    // The param answers for the render that carries it: the effect above has not run
    // yet, and the routes it reveals are decided on that same render.
    if (param !== null) {
        return param === 'true';
    }
    return Boolean(storageService.get(NEXT_PREVIEW_STORAGE_KEY, inSession));
};

export default useNextPreview;
